import { useAuth, useTranslation } from '@/context/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getTrips, getTasks, getFamilies, getTodayAgenda, getComplianceAlerts } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Map, ClipboardList, ShieldCheck, Wallet, Plus, ArrowRight, Calendar, Users, Plane, Sparkles, CheckCircle, AlertTriangle, Clock, Sun, Sunset, Moon, MapPin, TrendingUp, Zap, Compass, Briefcase, LayoutGrid, LayoutList } from 'lucide-react';

// Instant Pulse - Feature-flaggable import
import { QuickDeckFAB, FEATURE_FLAGS } from '@/components/quickdeck';
// Scan Here Modal
import { ScanHereModal } from '@/components/ScanHereModal';
// Role-based Dashboards
import OrganizerDashboard from '@/components/dashboards/OrganizerDashboard';
import VisitorDashboard from '@/components/dashboards/VisitorDashboard';
import KidsDashboard from '@/components/dashboards/KidsDashboard';
import { useRole } from '@/context/RoleContext';
import ModeSelector from '@/components/ModeSelector';

const DESTINATION_IMAGES = {
  default: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  lisbon: 'https://images.unsplash.com/photo-1585208798174-6cedd86e019a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  paris: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  barcelona: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  london: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  rome: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  tokyo: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  new_york: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
};

function getDestImage(destination) {
  if (!destination) return DESTINATION_IMAGES.default;
  const lower = destination.toLowerCase();
  for (const [key, url] of Object.entries(DESTINATION_IMAGES)) {
    if (lower.includes(key.replace('_', ' '))) return url;
  }
  return DESTINATION_IMAGES.default;
}

export default function Dashboard() {
  const { user, families, setFamilies } = useAuth();
  const { t, language } = useTranslation();
  const [trips, setTrips] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Role-based view toggle
  const [useRoleView, setUseRoleView] = useState(false);
  
  // Get role from context
  const { role, canSwitchMode, switchMode, loading: roleLoading } = useRole();
  
  // Get active trip for reference
  const activeTrip = trips.find(t => t.status !== 'completed' && t.status !== 'cancelled');

  useEffect(() => {
    const load = async () => {
      try {
        const famRes = await getFamilies();
        setFamilies(famRes.data);
        if (famRes.data.length > 0) {
          const [tripRes, taskRes] = await Promise.all([
            getTrips(famRes.data[0].family_id),
            getTasks({ family_id: famRes.data[0].family_id })
          ]);
          setTrips(tripRes.data);
          setTasks(taskRes.data);
          getTodayAgenda().then(r => setAgenda(r.data)).catch(() => {});
          if (tripRes.data.length > 0) {
            const activeTrip = tripRes.data.find(t => t.status !== 'completed' && t.status !== 'cancelled');
            if (activeTrip) getComplianceAlerts(activeTrip.trip_id).then(r => setAlerts(r.data)).catch(() => {});
          }
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [setFamilies]);

  const upcomingTrips = trips.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
  const pendingTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');
  const totalCompliance = trips.reduce((a, t) => a + (t.compliance_total || 0), 0);
  const okCompliance = trips.reduce((a, t) => a + (t.compliance_ok || 0), 0);

  // Scan Here Modal state
  const [showScanModal, setShowScanModal] = useState(false);

  const handleScanHere = useCallback(() => {
    setShowScanModal(true);
  }, []);

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return language === 'fr' ? 'Bonjour' : 'Good morning';
    if (hour < 18) return language === 'fr' ? 'Bon apres-midi' : 'Good afternoon';
    return language === 'fr' ? 'Bonsoir' : 'Good evening';
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="h-8 w-56 bg-slate-200 rounded-lg animate-pulse" />
        <div className="h-48 bg-white rounded-2xl animate-pulse border border-slate-100" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse border border-slate-100" />)}</div>
      </div>
    );
  }

  // Render role-based dashboard if enabled
  if (useRoleView && !roleLoading && role) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-page-role-based">
        {/* Toggle back to classic view */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-purple-900">{getGreeting()}, {user?.name?.split(' ')[0]}</h1>
            {canSwitchMode && <ModeSelector currentRole={role} canSwitch={canSwitchMode} onSwitchMode={switchMode} />}
          </div>
          <Button 
            onClick={() => setUseRoleView(false)} 
            variant="outline" 
            className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <LayoutList className="w-4 h-4 mr-2" />
            Vue Classique
          </Button>
        </div>

        {/* Render appropriate dashboard */}
        {role === 'organizer' && <OrganizerDashboard />}
        {role === 'visitor' && <VisitorDashboard />}
        {role === 'kids' && <KidsDashboard />}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header - Premium style */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-purple-900">{getGreeting()}, {user?.name?.split(' ')[0]}</h1>
          <p className="text-sm text-purple-500 mt-0.5">{t('dashboard.overview')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Toggle to role-based view */}
          {!roleLoading && role && (
            <Button 
              onClick={() => setUseRoleView(true)} 
              variant="outline" 
              className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Vue par Rôle
            </Button>
          )}
          <Link to="/trips/new">
            <Button className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white px-5 h-10 text-sm font-semibold shadow-lg shadow-purple-500/30" data-testid="new-trip-btn">
              <Plus className="w-4 h-4 mr-1.5" /> {t('trips.create')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero trip card - Premium dark style */}
      {activeTrip && (
        <Link to={`/trips/${activeTrip.trip_id}`}>
          <div className="relative rounded-2xl overflow-hidden cursor-pointer group shadow-xl" data-testid="active-trip-card">
            <div className="relative h-48 overflow-hidden">
              <img
                src={getDestImage(activeTrip.destination)}
                alt={activeTrip.destination}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90 via-purple-900/40 to-transparent" />
              
              {/* Live badge */}
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30">
                <div className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" />
                <span className="text-xs font-medium text-white">{t('live.now')}</span>
              </div>

              {/* Stats badges */}
              <div className="absolute top-4 right-4 flex gap-2">
                <Badge className="bg-white/20 backdrop-blur-md text-white border-0 text-xs rounded-lg px-3 font-medium">
                  <Plane className="w-3 h-3 mr-1" /> {activeTrip.bookings_count || 0}
                </Badge>
                <Badge className="bg-white/20 backdrop-blur-md text-white border-0 text-xs rounded-lg px-3 font-medium">
                  <Users className="w-3 h-3 mr-1" /> {activeTrip.travelers_count || 0}
                </Badge>
              </div>

              {/* Trip info */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h2 className="text-xl font-bold text-white mb-1">{activeTrip.name}</h2>
                <p className="text-sm text-white/70 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" />
                  {activeTrip.destination && `${activeTrip.destination}`}
                  {activeTrip.start_date && ` · ${new Date(activeTrip.start_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' })}`}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="bg-white p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-purple-500 font-medium">{language === 'fr' ? 'Preparation' : 'Progress'}</span>
                  <span className="text-xs font-bold text-purple-600">{activeTrip.tasks_done || 0}/{activeTrip.tasks_total || 0}</span>
                </div>
                <Progress value={activeTrip.tasks_total ? (activeTrip.tasks_done / activeTrip.tasks_total) * 100 : 0} className="h-1.5" />
              </div>
              {(() => {
                const daysUntil = activeTrip.start_date ? Math.ceil((new Date(activeTrip.start_date) - new Date()) / 86400000) : null;
                return daysUntil > 0 ? (
                  <Badge className="bg-purple-50 text-purple-600 border-0 text-xs rounded-lg px-3 py-1.5 font-bold">
                    <Clock className="w-3 h-3 mr-1" /> J-{daysUntil}
                  </Badge>
                ) : daysUntil === 0 ? (
                  <Badge className="bg-pink-50 text-pink-600 border-0 text-xs rounded-lg px-3 py-1.5 font-bold">
                    <Zap className="w-3 h-3 mr-1" /> {language === 'fr' ? "C'est parti !" : "Let's go!"}
                  </Badge>
                ) : null;
              })()}
            </div>
          </div>
        </Link>
      )}

      {/* Stats - Clean premium cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Map, label: t('dashboard.stats.trips'), value: upcomingTrips.length, color: 'bg-gradient-to-br from-purple-500 to-purple-600', textColor: 'text-purple-600' },
          { icon: ClipboardList, label: t('dashboard.stats.tasks'), value: `${doneTasks.length}/${tasks.length}`, color: 'bg-gradient-to-br from-pink-500 to-pink-600', textColor: 'text-pink-600' },
          { icon: ShieldCheck, label: t('dashboard.stats.compliance'), value: totalCompliance > 0 ? `${Math.round((okCompliance / totalCompliance) * 100)}%` : '--', color: 'bg-gradient-to-br from-emerald-500 to-emerald-600', textColor: 'text-emerald-600' },
          { icon: Wallet, label: t('dashboard.stats.budget'), value: `${trips.length}`, color: 'bg-gradient-to-br from-amber-500 to-amber-600', textColor: 'text-amber-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 border border-purple-100 hover:border-purple-200 hover:shadow-lg hover:shadow-purple-100 transition-all" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl ${s.color} flex items-center justify-center shadow-lg`}>
                <s.icon className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>
              <TrendingUp className={`w-4 h-4 ${s.textColor}`} />
            </div>
            <p className="text-2xl font-bold text-purple-900">{s.value}</p>
            <p className="text-xs font-medium text-purple-400 uppercase tracking-wider mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Three columns - Premium style */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Upcoming trips */}
        <div className="bg-white rounded-2xl border border-purple-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-purple-100 flex items-center justify-between">
            <h2 className="text-sm font-bold text-purple-900 flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <Map className="w-3.5 h-3.5 text-white" />
              </div>
              {t('dashboard.upcomingTrips')}
            </h2>
            <Link to="/trips">
              <Button variant="ghost" size="sm" className="h-7 text-xs text-purple-600 hover:bg-purple-50 rounded-lg font-medium">
                {language === 'fr' ? 'Voir tout' : 'View all'} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="p-3">
            {upcomingTrips.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
                  <Compass className="w-8 h-8 text-purple-400" />
                </div>
                <p className="text-sm text-purple-500 mb-3">{t('dashboard.noTrips')}</p>
                <Link to="/trips/new">
                  <Button size="sm" className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs h-9 px-4 font-medium">
                    {t('dashboard.createFirst')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-1">
                {upcomingTrips.slice(0, 4).map(trip => {
                  const daysUntil = trip.start_date ? Math.ceil((new Date(trip.start_date) - new Date()) / 86400000) : null;
                  return (
                    <Link key={trip.trip_id} to={`/trips/${trip.trip_id}`}>
                      <div className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-purple-50/50 transition-colors group">
                        <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
                          <img src={getDestImage(trip.destination)} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-purple-900 truncate group-hover:text-purple-600 transition-colors">{trip.name}</p>
                          <p className="text-xs text-purple-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {trip.destination}
                          </p>
                        </div>
                        {daysUntil !== null && daysUntil >= 0 && (
                          <Badge className={`border-0 text-xs font-bold rounded-lg px-2.5 ${
                            daysUntil === 0 ? 'bg-pink-100 text-pink-700' : 'bg-purple-50 text-purple-600'
                          }`}>
                            {daysUntil === 0 ? (language === 'fr' ? 'Auj.' : 'Today') : `J-${daysUntil}`}
                          </Badge>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Today's agenda */}
        <div className="bg-white rounded-2xl border border-purple-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-purple-100 flex items-center justify-between">
            <h2 className="text-sm font-bold text-purple-900 flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center">
                <Calendar className="w-3.5 h-3.5 text-white" />
              </div>
              {t('dashboard.liveNow')}
            </h2>
            <Badge className="bg-purple-50 text-purple-600 border-0 text-xs rounded-lg font-medium">
              {new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', day: 'numeric' })}
            </Badge>
          </div>
          <div className="p-3">
            {agenda.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
                  <Sun className="w-8 h-8 text-purple-400" />
                </div>
                <p className="text-sm text-purple-500">{language === 'fr' ? 'Rien de prevu aujourd\'hui' : 'Nothing planned today'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {agenda.slice(0, 5).map((item, i) => {
                  const Icon = item.time_slot === 'morning' ? Sun : item.time_slot === 'afternoon' ? Sunset : Moon;
                  return (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-purple-50/50">
                      <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm">
                        <Icon className="w-4 h-4 text-pink-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-purple-900 truncate">{item.title}</p>
                        <p className="text-xs text-purple-500">{item.time || item.time_slot}</p>
                      </div>
                      {item.is_live && (
                        <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Pending tasks */}
        <div className="bg-white rounded-2xl border border-purple-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-purple-100 flex items-center justify-between">
            <h2 className="text-sm font-bold text-purple-900 flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <ClipboardList className="w-3.5 h-3.5 text-white" />
              </div>
              {t('dashboard.recentTasks')}
            </h2>
            <Link to="/tasks">
              <Button variant="ghost" size="sm" className="h-7 text-xs text-purple-600 hover:bg-purple-50 rounded-lg font-medium">
                {language === 'fr' ? 'Voir tout' : 'View all'} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="p-3">
            {pendingTasks.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-purple-400" />
                </div>
                <p className="text-sm text-purple-500">{language === 'fr' ? 'Tout est fait !' : 'All done!'}</p>
              </div>
            ) : (
              <div className="space-y-1">
                {pendingTasks.slice(0, 5).map(task => (
                  <div key={task.task_id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-purple-50/50 transition-colors">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      task.priority === 'high' ? 'bg-pink-100' : task.priority === 'medium' ? 'bg-amber-100' : 'bg-purple-50'
                    }`}>
                      <Zap className={`w-4 h-4 ${
                        task.priority === 'high' ? 'text-pink-600' : task.priority === 'medium' ? 'text-amber-600' : 'text-purple-500'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-purple-900 truncate">{task.title}</p>
                      {task.due_date && (
                        <p className="text-xs text-purple-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(task.due_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short' })}
                        </p>
                      )}
                    </div>
                    <Badge className={`border-0 text-xs rounded-lg px-2 ${
                      task.status === 'in_progress' ? 'bg-purple-100 text-purple-700' : 'bg-purple-50 text-purple-600'
                    }`}>
                      {task.status === 'in_progress' ? (language === 'fr' ? 'En cours' : 'In progress') : (language === 'fr' ? 'A faire' : 'To do')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alerts section - if any */}
      {alerts.length > 0 && (
        <div className="bg-pink-50 rounded-2xl p-5 border border-pink-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-purple-900">{language === 'fr' ? 'Points d\'attention' : 'Attention points'}</h3>
              <p className="text-xs text-purple-600">{alerts.length} {language === 'fr' ? 'elements a verifier' : 'items to check'}</p>
            </div>
          </div>
          <div className="space-y-2">
            {alerts.slice(0, 3).map((alert, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl">
                <AlertTriangle className="w-4 h-4 text-pink-500 flex-shrink-0" />
                <p className="text-sm text-purple-700">{alert.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick actions - Premium style */}
      <div className="gradient-airhelp rounded-2xl p-6 mb-32">
        <h3 className="text-white font-bold mb-4">{t('dashboard.quickActions')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Plus, label: language === 'fr' ? 'Nouveau plan' : 'New plan', to: '/trips/new', color: 'from-purple-500 to-purple-600' },
            { icon: Map, label: language === 'fr' ? 'Voir mes plans' : 'View plans', to: '/trips', color: 'from-pink-500 to-pink-600' },
            { icon: ClipboardList, label: language === 'fr' ? 'Mes actions' : 'My actions', to: '/tasks', color: 'from-amber-500 to-amber-600' },
            { icon: Sparkles, label: language === 'fr' ? 'Assistant IA' : 'AI Assistant', to: activeTrip ? `/trips/${activeTrip.trip_id}?tab=copilot` : '/trips', color: 'from-emerald-500 to-emerald-600' },
          ].map((action, i) => (
            <Link key={i} to={action.to}>
              <div className="bg-white/10 hover:bg-white/20 rounded-xl p-4 transition-all group cursor-pointer">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-lg`}>
                  <action.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-medium text-white">{action.label}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Instant Pulse FAB - Simple floating button */}
      {FEATURE_FLAGS.quickdeck && (
        <QuickDeckFAB onScanHere={handleScanHere} />
      )}

      {/* Scan Here Modal */}
      {showScanModal && (
        <ScanHereModal 
          isOpen={showScanModal} 
          onClose={() => setShowScanModal(false)}
        />
      )}
    </div>
  );
}
